import { Alert, Box, Button, Stack, TextField } from "@mui/material";
import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createIngressTokenForService } from "../api/client";
import { Panel } from "../components/layout/Panel";
import { PageErrorState } from "../components/pages/PageErrorState";
import { useHierarchyContext } from "../contexts/HierarchyContext";

export function PageIngressTokenNew() {
  const navigate = useNavigate();
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;

  const [name, setName] = useState("default");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!serviceSlug) return;

    try {
      setLoading(true);
      setError(null);
      await createIngressTokenForService(
        serviceSlug,
        { name: name.trim() || "default" },
        undefined,
      );
      navigate(`/services/${encodeURIComponent(serviceSlug)}/ingress-tokens`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create ingress token");
    } finally {
      setLoading(false);
    }
  };

  if (!serviceSlug) {
    return (
      <Alert severity="warning">
        Missing service in URL. Please navigate from a service overview page.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Panel
        title="Create Ingress Token"
        subtitle={`Service: ${serviceSlug}`}
        toolbar={
          <Stack direction="row" spacing={1}>
            <Button variant="text" onClick={() => navigate(-1)} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              form="ingress-token-new-form"
              disabled={loading}
            >
              Create
            </Button>
          </Stack>
        }
      >
        {error ? <PageErrorState message={error} sx={{ mb: 1.5 }} /> : null}
        <Box
          id="ingress-token-new-form"
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            maxWidth: 720,
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Token name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="default"
          />
        </Box>
      </Panel>

      <Alert severity="info">
        The created token will appear on the service overview page. Copy it once and keep it safe.
      </Alert>
    </Box>
  );
}
